/**
 * CR306: Hook for running and loading recipe test runs
 */

import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { RecipeTestRun, RecipeTestStep } from "@/lib/recipes-studio/config-types";

export function useRecipeTestRunner() {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<RecipeTestRun | null>(null);
  const [testHistory, setTestHistory] = useState<RecipeTestRun[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const runTest = useCallback(async (recipeId: string, urlOverride?: string, context?: Record<string, string>) => {
    setTesting(true);
    setTestResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("recipe-test-crawl", {
        body: { recipe_id: recipeId, url_override: urlOverride, context },
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || "Test run failed");
      }

      const result = data.test_run as RecipeTestRun;
      setTestResult(result);
      toast.success(`Test ${result.status === "COMPLETE" ? "passed" : "failed"}`);
      return result;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Test run failed";
      console.error("[CR306] Test run error:", err);
      toast.error(msg);
      return null;
    } finally {
      setTesting(false);
    }
  }, []);

  const loadHistory = useCallback(async (recipeId: string) => {
    setLoadingHistory(true);
    try {
      // Use rpc-style query with explicit any cast since these tables
      // aren't in the auto-generated types
      const { data, error } = await (supabase as any)
        .from("recipe_test_runs")
        .select("*")
        .eq("recipe_id", recipeId)
        .eq("source_type", "web_crawl")
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;

      const runs = (data || []) as RecipeTestRun[];
      if (runs.length > 0) {
        const runIds = runs.map((r) => r.id);
        const { data: stepsData } = await (supabase as any)
          .from("recipe_test_steps")
          .select("*")
          .in("test_run_id", runIds)
          .order("step_index", { ascending: true });

        const stepsByRun = new Map<string, RecipeTestStep[]>();
        for (const step of (stepsData || []) as (RecipeTestStep & { test_run_id: string })[]) {
          const existing = stepsByRun.get(step.test_run_id) || [];
          existing.push(step);
          stepsByRun.set(step.test_run_id, existing);
        }

        for (const run of runs) {
          run.steps = stepsByRun.get(run.id) || [];
        }
      }

      setTestHistory(runs);
    } catch (err) {
      console.error("[CR306] Load history error:", err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  return {
    testing,
    testResult,
    testHistory,
    loadingHistory,
    runTest,
    loadHistory,
    clearResult: () => setTestResult(null),
  };
}
